/**
 * Comment parser — maps OOXML comment ranges directly to ProseMirror comment marks.
 *
 * In OOXML, comments are defined by:
 *   - <w:commentRangeStart w:id="N"/> — marks where the comment highlight begins
 *   - <w:commentRangeEnd w:id="N"/>   — marks where the comment highlight ends
 *   - <w:commentReference w:id="N"/>  — inside a run, links to the comment (usually at end)
 *   - word/comments.xml              — contains the actual comment text and metadata
 *
 * These elements are siblings of <w:r> within <w:p>. Our approach:
 *   1. Track which comment IDs are "active" (between rangeStart and rangeEnd)
 *   2. For each text node produced, apply comment marks for all active comment IDs
 *   3. Support overlapping comments (multiple active at same time — TipTap allows this)
 *   4. Handle "point comments" (commentReference with no range) as zero-width marks
 *
 * This replaces the mammoth adapter's Unicode marker injection approach entirely.
 *
 * TipTap comment mark attrs:
 *   { type: "comment", attrs: { commentId: "comment-{timestamp}-{random}" } }
 */

import { PMMark, ParseContext, ParsedComment } from './types';

/** State tracker for active comment ranges during paragraph parsing. */
export class CommentTracker {
  /** Currently active comment XML IDs (between rangeStart and rangeEnd) */
  private activeCommentIds: Set<string> = new Set();

  /** Map from OOXML comment w:id → generated TipTap commentId */
  private commentIdMap: Map<string, string> = new Map();

  /** Collected comment metadata for the result */
  private parsedComments: Map<string, ParsedComment> = new Map();

  /** Collected commented text snippets per comment ID */
  private commentedTextMap: Map<string, string> = new Map();

  constructor(private ctx: ParseContext) {}

  /**
   * Handle a <w:commentRangeStart> element.
   * Activates the comment so subsequent text nodes get the comment mark.
   */
  handleCommentRangeStart(id: string): void {
    this.activeCommentIds.add(id);
    this.ensureCommentId(id);
  }

  /**
   * Handle a <w:commentRangeEnd> element.
   * Deactivates the comment.
   */
  handleCommentRangeEnd(id: string): void {
    this.activeCommentIds.delete(id);
  }

  /**
   * Handle a <w:commentReference> element (inside a run).
   * For point comments (no range), this is the only indicator.
   * We ensure the comment is registered even if no range was seen.
   */
  handleCommentReference(id: string): void {
    this.ensureCommentId(id);
  }

  /**
   * Get comment marks that should be applied to text nodes at the current position.
   * Returns marks for all currently active comment ranges.
   */
  getActiveCommentMarks(): PMMark[] {
    const marks: PMMark[] = [];
    Array.from(this.activeCommentIds).forEach((xmlId) => {
      const tiptapId = this.commentIdMap.get(xmlId);
      if (tiptapId) {
        marks.push({
          type: 'comment',
          attrs: { commentId: tiptapId },
        });
      }
    });
    return marks;
  }

  /**
   * Record text that falls within a comment range (for commentedText metadata).
   */
  recordText(text: string): void {
    Array.from(this.activeCommentIds).forEach((xmlId) => {
      const existing = this.commentedTextMap.get(xmlId) || '';
      this.commentedTextMap.set(xmlId, existing + text);
    });
  }

  /**
   * Get all parsed comments with their metadata.
   *
   * Returns a flat list of ALL comments (including replies). Reply comments
   * have a `parentCommentId` field linking them to their parent's TipTap ID.
   * This ensures every comment with range markers in the document has a
   * matching entry in the output — no "ghost" comments from missing metadata
   * and no lost replies from over-eager nesting.
   */
  getParsedComments(): ParsedComment[] {
    // Finalize commented text
    Array.from(this.parsedComments.entries()).forEach(([xmlId, comment]) => {
      const commentedText = this.commentedTextMap.get(xmlId);
      if (commentedText) {
        comment.commentedText = commentedText.trim();
      }
    });

    const allComments = Array.from(this.parsedComments.entries());
    const replyMap = this.ctx.commentReplyMap;

    // Set parentCommentId on reply comments using the reply map
    if (replyMap && replyMap.size > 0) {
      Array.from(replyMap.entries()).forEach(([childXmlId, parentXmlId]) => {
        const childComment = this.parsedComments.get(childXmlId);
        const parentTiptapId = this.commentIdMap.get(parentXmlId);
        if (childComment && parentTiptapId) {
          childComment.parentCommentId = parentTiptapId;
        }
      });
    }

    // Return flat list of all comments (replies included with parentCommentId set)
    return allComments.map(([, c]) => c);
  }

  /**
   * Ensure a TipTap comment ID exists for the given OOXML comment ID.
   * Creates the ParsedComment entry from word/comments.xml metadata.
   */
  private ensureCommentId(xmlId: string): void {
    if (this.commentIdMap.has(xmlId)) return;

    const tiptapId = generateCommentId();
    this.commentIdMap.set(xmlId, tiptapId);

    // Look up comment metadata from word/comments.xml
    const commentDef = this.ctx.comments.get(xmlId);
    this.parsedComments.set(xmlId, {
      id: tiptapId,
      originalId: xmlId,
      author: commentDef?.author || 'Unknown',
      date: commentDef?.date || '',
      content: commentDef?.content || '',
    });
  }
}

/** Generate a unique TipTap comment ID. */
let commentCounter = 0;
export function generateCommentId(): string {
  commentCounter++;
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 100000);
  return `comment-${timestamp}-${random}-${commentCounter}`;
}

/** Reset counter (for testing). */
export function resetCommentCounter(): void {
  commentCounter = 0;
}
